/**
 * KeyboardBar — input accessory bar (44px) that sits flush above the keyboard.
 *
 * Props:
 *   charCount  — current textarea character count
 *   minChars   — minimum characters required to enable submit
 *   bottom     — CSS bottom offset (keyboardHeight px, passed by UgcTextScreen)
 *   onSubmit   — called when user taps "Готово"
 *   disabled?  — force-disable the button (e.g. while submitting)
 *
 * Used by: UgcTextScreen
 * ADR-027: no hex literals — all colors via P/S/T tokens.
 */

import { P, S, T } from '../../../styles/tokens';

export interface KeyboardBarProps {
  charCount: number;
  minChars: number;
  bottom: number;
  onSubmit: () => void;
  disabled?: boolean;
}

export function KeyboardBar({ charCount, minChars, bottom, onSubmit, disabled }: KeyboardBarProps) {
  const enough = charCount >= minChars;
  const counterColor = enough ? P.success : charCount > 0 ? P.warn : S.tertiaryLabel;
  const isDisabled = disabled || !enough;

  return (
    <div
      data-testid="keyboard-bar"
      data-enough={enough ? 'true' : 'false'}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px 0 14px',
        background: '#EFEDE6',
        borderTop: `1px solid ${P.line}`,
        boxShadow: '0 -1px 0 rgba(0,0,0,0.04)',
        zIndex: 100,
      }}
    >
      <span
        data-testid="keyboard-bar-counter"
        style={{ fontVariantNumeric: 'tabular-nums', font: T.caption, color: S.secondaryLabel }}
      >
        <b style={{ color: counterColor, fontFamily: T.fontMono }}>{charCount}</b>
        <span style={{ color: P.muted }}> / {minChars}</span>
        {enough && (
          <span style={{ color: P.success, marginLeft: 6, fontWeight: 700 }}>
            {' '}хватит
          </span>
        )}
      </span>

      <button
        data-testid="keyboard-bar-submit"
        onClick={onSubmit}
        disabled={isDisabled}
        style={{
          height: 32, padding: '0 14px', borderRadius: 16,
          background: isDisabled ? 'transparent' : P.terracotta,
          color: isDisabled ? S.tertiaryLabel : '#fff',
          border: 'none',
          display: 'flex', alignItems: 'center', gap: 6,
          font: T.footnote, fontWeight: 700, fontFamily: T.fontBody,
          boxShadow: isDisabled ? 'none' : '0 2px 6px rgba(192,81,58,0.30)',
          cursor: isDisabled ? 'default' : 'pointer',
        }}
      >
        Отправить
      </button>
    </div>
  );
}
